import React, { useState, useRef, CSSProperties, useCallback, useMemo, useEffect } from 'react';
import styles from './mobile-navigation.module.scss';
import MobileNavigationItem from './mobile-navigation-item';
import Image from 'next/image';
import { CrossIcon, INTOWhiteLogo } from '../../atoms/icons/icons';
import Link from 'next/link';
import Button from '../../atoms/button/button';
import { ButtonStyleType } from '../../../constants/button-style-type';
import { ButtonSizeType } from '../../../constants/button-size-type';
import useOnClickOutside from '../../../hooks/use-on-click-outside.hook';
import IMenuItem from '../../../models/header/IMenuItem';
import LanguageSwitcher from '../../atoms/language-switcher/language-switcher';
import { ApplicationType } from '../../../constants/application-type';
import colorStyle from '../../../styles/utils/variables.module.scss';

type Locale = { text: string; value: string };

export type MobileNavigationProps = {
  items: IMenuItem[];
  currentLocale: string;
  brandLogo?: string;
  brandLogoAltText?: string;
  themeColor?: string;
  themeCopyColor?: string;
  invertedTheme?: boolean;
  brandPrimaryColor?: string;
  headerApplyURL?: string;
  headerApplyCopyText?: string;
  isVisible?: boolean;
  openSideMenu: boolean;
  locales: Locale[];
  applicationType: ApplicationType;
};

const MobileNavigation: React.FC<MobileNavigationProps> = ({
  items = [],
  currentLocale,
  brandLogo,
  brandLogoAltText = 'Brand Logo',
  themeColor,
  themeCopyColor,
  invertedTheme,
  brandPrimaryColor,
  headerApplyURL,
  headerApplyCopyText,
  isVisible = false,
  openSideMenu,
  locales,
  applicationType,
}) => {
  const [isMenuVisible, setMenuVisible] = useState(isVisible);
  const [openMenus, setOpenMenus] = useState<Set<string>>(new Set());
  const sidebarRef = useRef<HTMLDivElement | null>(null);

  const toggleMenu = useCallback(() => {
    setMenuVisible((prev) => {
      if (!prev) setOpenMenus(new Set()); // Reset submenus when opening the menu
      return !prev;
    });
  }, []);

  const toggleSubmenu = useCallback((id: string, isExpanded: boolean, parentId = '') => {
    setOpenMenus((prev) => {
      const updated = new Set<string>();
      if (!parentId) {
        if (!isExpanded) updated.add(id);
      } else {
        const siblingPrefix = `${parentId}-`;
        for (const menuId of prev) {
          if (!menuId.startsWith(siblingPrefix)) updated.add(menuId);
        }
        if (!isExpanded) updated.add(id);
      }
      return updated;
    });
  }, []);

  const closeAllMenus = useCallback(() => {
    setOpenMenus(new Set());
    setMenuVisible(false);
  }, []);

  // Using hook to close sub-menus when clicking outside
  useOnClickOutside(sidebarRef, closeAllMenus);

  const renderStyles = useMemo(() => {
    return {
      sidebar: {
        backgroundColor: themeColor,
        color: themeCopyColor,
        '--menu-color': themeColor,
        '--menu-copy-color': themeCopyColor,
        '--brand-primary-color': brandPrimaryColor,
      } as CSSProperties,
      mobileTrigger: {
        '--menu-color': themeColor,
        '--menu-copy-color': themeCopyColor,
        '--brand-primary-color': brandPrimaryColor,
      } as CSSProperties,
    };
  }, [themeColor, themeCopyColor, brandPrimaryColor]);

  useEffect(() => {
    if (openSideMenu) {
      toggleMenu();
    }
  }, [openSideMenu, toggleMenu]);

  return (
    <>
      {/* Sidebar Menu */}
      {isMenuVisible && (
        <aside
          ref={sidebarRef}
          className={`${styles['sidebar']} ${styles['mobile-navigation']} ${invertedTheme ? styles['inverted-theme'] : ''}`}
          style={renderStyles.sidebar}
          role='menu'
        >
          {/* Logo Section */}
          <div
            className={`${styles['mobile-navigation__header']} d-flex align-items-center w-100 justify-content-between`}
          >
            <Link
              href={`/${currentLocale}`}
              onClick={toggleMenu}
              className={`d-flex justify-content-between ${styles['mobile-navigation__logo']} ${invertedTheme ? styles['inverted-theme'] : ''}`}
            >
              {brandLogo ? (
                <Image src={brandLogo} alt={brandLogoAltText} width={230} height={40} />
              ) : (
                <INTOWhiteLogo />
              )}
            </Link>
            <button
              type='button'
              className={`${styles['mobile-trigger']} ${invertedTheme ? styles['inverted-theme'] : ''} d-lg-none image-wrapper`}
              title='Toggle Mobile Navigation'
              style={renderStyles.mobileTrigger}
              onClick={toggleMenu}
            >
              <CrossIcon fillColor={colorStyle.white} height={24} width={24} />
            </button>
          </div>

          {/* Navigation Items */}
          <nav aria-label='Mobile Navigation' className={`${styles['mobile-navigation__nav']}`}>
            {items.length > 0 && (
              <ul className={`${styles['mobile-navigation__nav__list']} p-0 m-0`}>
                {items.map((item, index) => (
                  <MobileNavigationItem
                    key={`${index}--${item.text || item.url}`}
                    item={item}
                    toggleSubMenu={toggleSubmenu}
                    toggleMenu={toggleMenu}
                    openMenus={openMenus}
                    parentId=''
                  />
                ))}
              </ul>
            )}
          </nav>

          <div
            className={`${styles['mobile-navigation__footer']} d-flex justify-content-between align-items-center`}
          >
            <LanguageSwitcher
              locales={locales}
              activeLocale={currentLocale}
              themeCopyColor={themeCopyColor}
              brandPrimaryColor={brandPrimaryColor}
              application={applicationType}
              invertedTheme={invertedTheme}
              customClass='language-switcher-mobile'
            />
            {headerApplyURL && headerApplyCopyText && (
              <div className={`${styles['mobile-navigation__nav__apply']} d-inline-flex`}>
                <Button
                  buttonID='header-apply-cta'
                  buttonAriaLabel='cta to help students apply to offered courses'
                  buttonName='apply cta'
                  buttonStyle={ButtonStyleType.Outline}
                  buttonSize={ButtonSizeType.Medium}
                  buttonURL={headerApplyURL}
                  copyColor={themeCopyColor ? themeCopyColor : colorStyle.white}
                  outlineColor={themeCopyColor ? themeCopyColor : colorStyle.white}
                  backgroundColor={colorStyle.transparent}
                  hoverBackgroundColor={themeCopyColor ? themeCopyColor : colorStyle.white}
                  activeBackgroundColor={colorStyle.transparent}
                  buttonRole='button'
                  customClass='header-apply-cta'
                >
                  {headerApplyCopyText}
                </Button>
              </div>
            )}
          </div>
        </aside>
      )}
    </>
  );
};

export default MobileNavigation;
